/**
 * The chef board: one full-height column per station that has something in it.
 *
 * Same shape as the by-table board, which the kitchen already reads every day —
 * a row of full-height cards that scrolls sideways, with a button at each end
 * and a sideways drag (`lib/drag-scroll.ts`), because the till reports a finger
 * as a mouse and a plain swipe moves nothing.
 *
 * A station with nothing in it is not drawn. Four earlier designs kept the
 * empty ones on the board in one form or another, and every one of them paid
 * for it in the height of the queue; the numbers are in `station-layout.ts`.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import StationColumn from "./StationColumn";
import { columnWidthPx, type StationLoad } from "../lib/station-layout";
import { type KdsScale } from "../lib/kds-scale";
import { useDragScroll } from "../hooks/useDragScroll";
import { useTranslation } from "../i18n";

interface StationBoardProps {
  columns: StationLoad[];
  viewportWidth: number;
  /** The type size chosen on this monitor: the columns follow it. */
  scale: KdsScale;
  /** The venue's order, for deciding which chevrons a station may show. */
  stations: any[];
  onMoveStation: (stationId: string, direction: "left" | "right") => void;
}

export default function StationBoard({
  columns,
  viewportWidth,
  scale,
  stations,
  onMoveStation,
}: StationBoardProps) {
  const { t } = useTranslation();
  // Measured, moved by the two arrows, and dragged sideways.
  const scrollRef = useDragScroll<HTMLDivElement>("x");
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [measure, columns.length, viewportWidth, scale]);

  if (stations.length === 0) {
    return (
      <div className="text-center text-kds-muted py-20">
        <p className="text-kds-title">{t("kds.board.noStations", "Nessuna stazione configurata")}</p>
        <p className="mt-2">{t("kds.board.configureStations", "Configura le stazioni dal CRM")}</p>
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-kds-muted gap-2">
        <p className="text-kds-title">{t("kds.board.noStationsWorking", "Nessuna postazione in lavorazione")}</p>
        <p className="text-kds-note">
          {t("kds.board.stationsAppearHint", "Le postazioni appaiono qui quando hanno qualcosa da preparare")}
        </p>
      </div>
    );
  }

  const width = columnWidthPx(viewportWidth, columns.length, scale);
  const step = width + 16;

  const arrow = (side: "left" | "right") => {
    const enabled = side === "left" ? canLeft : canRight;
    const Icon = side === "left" ? ChevronLeft : ChevronRight;
    return (
      <button
        type="button"
        aria-label={side === "left" ? t("kds.board.scrollLeft", "Scorri a sinistra") : t("kds.board.scrollRight", "Scorri a destra")}
        disabled={!enabled}
        onClick={() =>
          scrollRef.current?.scrollBy({
            left: side === "left" ? -step : step,
            behavior: "smooth",
          })
        }
        className={[
          "absolute top-1/2 -translate-y-1/2 z-20 w-12 h-24",
          "flex items-center justify-center",
          side === "left" ? "left-0 rounded-r-xl" : "right-0 rounded-l-xl",
          enabled
            ? "bg-kds-preparing text-white shadow-xl active:scale-95"
            : "bg-kds-card/60 text-kds-muted cursor-default",
        ].join(" ")}
      >
        <Icon size={36} strokeWidth={3} />
      </button>
    );
  };

  return (
    // min-h-0 is load-bearing: without it the flex item grows with its content
    // and the columns leave the screen instead of scrolling.
    <div className="flex-1 min-h-0 relative overflow-hidden flex">
      {arrow("left")}
      <div
        ref={scrollRef}
        className="flex-1 min-w-0 h-full overflow-x-auto overflow-y-hidden"
        style={{ scrollbarWidth: "thin" }}
      >
        <div className="flex gap-4 h-full" style={{ minWidth: "max-content" }}>
          {columns.map((load) => {
            const idx = stations.findIndex((s: any) => s.id === load.station.id);
            return (
              <div
                key={load.station.id}
                className="flex-shrink-0 h-full overflow-hidden"
                style={{ width }}
              >
                <StationColumn
                  station={load.station}
                  orders={load.orders}
                  canMoveLeft={idx > 0}
                  canMoveRight={idx < stations.length - 1}
                  onMoveLeft={() => onMoveStation(load.station.id, "left")}
                  onMoveRight={() => onMoveStation(load.station.id, "right")}
                />
              </div>
            );
          })}
        </div>
      </div>
      {arrow("right")}
    </div>
  );
}
